import React, { useState, useEffect } from "react";
import { Modal, Button, Space, Input, Checkbox, Select, Radio, Tooltip, message } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleFilled,
  LoadingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { configApi, tenantApi } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

const ENV_META = {
  dev:   { label: "Development", short: "DEV" },
  stage: { label: "Staging",     short: "STG" },
  prod:  { label: "Production",  short: "PROD" },
};

const ALL_ENVS = ["dev", "stage", "prod"];

function StatusIcon({ status }) {
  if (status === "created" || status === "updated" || status === "success")
    return <CheckCircleFilled className="text-emerald-600 text-[15px]" />;
  if (status === "partial")
    return <WarningOutlined className="text-amber-600 text-[15px]" />;
  if (status === "skipped")
    return <MinusCircleFilled className="text-slate-400 text-[15px]" />;
  if (status === "error")
    return <CloseCircleFilled className="text-red-600 text-[15px]" />;
  if (status === "loading")
    return <LoadingOutlined className="text-indigo-500 text-[15px]" />;
  return null;
}

const STATUS_TEXT = { error: "text-red-600", partial: "text-amber-600", skipped: "text-slate-400" };

export default function CopyToEnvsModal({ visible, onCancel, record, configType, currentEnv, tenantSchema }) {
  const { permissions } = useAuth();
  const isAlias = configType === "alias";
  const valueField = isAlias ? "alias_name" : "value";
  const sourceValue = record?.[valueField] || "";

  // Only show envs where some scope grants config:copy
  const otherEnvs = ALL_ENVS.filter(
    (e) =>
      e !== currentEnv &&
      permissions.some((p) => p.environment === e && p.role?.permissions?.includes("config:copy"))
  );

  // Per-env: { tenantSchema, tenants[], loadingTenants, loadError, value, selected }
  const [envStates, setEnvStates] = useState({});
  const [ifExists, setIfExists]   = useState("override"); // top-level: "override" | "skip"
  const [results, setResults]     = useState({});
  const [saving, setSaving]       = useState(false);
  const [done, setDone]           = useState(false);

  useEffect(() => {
    if (!visible) return;
    setResults({});
    setDone(false);
    setIfExists("override");

    const init = {};
    otherEnvs.forEach((env) => {
      init[env] = { tenantSchemas: [], tenantValues: {}, tenants: [], loadingTenants: true, loadError: false, selected: true };
    });
    setEnvStates(init);

    // Fetch writable tenant list for each env in parallel
    otherEnvs.forEach(async (env) => {
      try {
        const res = await tenantApi.getCopyTargetsForEnv(env);
        const tenants = res.data || [];
        setEnvStates((prev) => ({
          ...prev,
          [env]: {
            ...prev[env],
            tenants,
            loadingTenants: false,
            noTargets: tenants.length === 0,
            selected: prev[env]?.selected && tenants.length > 0,
          },
        }));
      } catch (err) {
        const data = err.response?.data || {};
        const detail = data.detail || data.message || data.error || "Failed to load tenants";
        setEnvStates((prev) => ({
          ...prev,
          [env]: { ...prev[env], tenants: [], loadingTenants: false, loadError: true, loadErrorMessage: detail, selected: false },
        }));
      }
    });
  }, [visible]); // eslint-disable-line

  const setEnvField = (env, field, val) => {
    setEnvStates((prev) => ({ ...prev, [env]: { ...prev[env], [field]: val } }));
  };

  const envUnavailable = (env) => envStates[env]?.loadError || envStates[env]?.noTargets;

  const handleCopy = async () => {
    const activeEnvs = otherEnvs.filter((e) => envStates[e]?.selected);
    const missing = activeEnvs.filter((e) => !envStates[e]?.tenantSchemas?.length);
    if (missing.length > 0) {
      message.error(`Please select at least one tenant for: ${missing.map((e) => ENV_META[e].label).join(", ")}`);
      return;
    }
    if (activeEnvs.length === 0) {
      message.warning("No environments selected");
      return;
    }

    setSaving(true);
    const emptySummary = () => ({ created: 0, updated: 0, skipped: 0, error: 0 });
    const loadingMap = {};
    otherEnvs.forEach((env) => {
      loadingMap[env] = envStates[env]?.selected
        ? { overall: "loading", perTenant: [], summary: emptySummary() }
        : { overall: "skipped", perTenant: [], summary: emptySummary() };
    });
    setResults(loadingMap);

    // One target row per (env, tenant) with that tenant's value override
    // (defaults to the source value). Backend already iterates targets.
    const targets = activeEnvs.flatMap((env) =>
      (envStates[env].tenantSchemas || []).map((tSchema) => ({
        env,
        tenantSchema: tSchema,
        action: ifExists, // top-level setting applies to all
        [valueField]: envStates[env]?.tenantValues?.[tSchema] ?? sourceValue,
      }))
    );

    try {
      const res = await configApi.copyToEnvs({
        name: record.name,
        alias_name: record.alias_name,
        value: record.value,
        is_active: record.is_active,
        config_type: configType,
        sourceEnv: currentEnv,
        sourceTenant: tenantSchema,
        targets,
      });

      // Aggregate per-env: collect per-tenant rows + summary + overall status.
      const resultMap = {};
      otherEnvs.forEach((e) => {
        resultMap[e] = { perTenant: [], summary: emptySummary(), overall: "skipped" };
      });
      (res.data.results || []).forEach((r) => {
        if (!resultMap[r.env]) resultMap[r.env] = { perTenant: [], summary: emptySummary(), overall: "skipped" };
        resultMap[r.env].perTenant.push({ tenant: r.tenantSchema, status: r.status, error: r.error || null });
        if (resultMap[r.env].summary[r.status] !== undefined) {
          resultMap[r.env].summary[r.status]++;
        }
      });
      otherEnvs.forEach((e) => {
        const r = resultMap[e];
        const s = r.summary;
        if (r.perTenant.length === 0) r.overall = "skipped";
        else if (s.error > 0 && (s.created + s.updated + s.skipped) > 0) r.overall = "partial";
        else if (s.error > 0) r.overall = "error";
        else if (s.created > 0 || s.updated > 0) r.overall = "success";
        else r.overall = "skipped";
      });
      setResults(resultMap);
      setDone(true);

      const errors = (res.data.results || []).filter((r) => r.status === "error");
      errors.length === 0
        ? message.success("Copied successfully")
        : message.warning(`Completed with ${errors.length} error(s)`);
    } catch (err) {
      message.error(err.response?.data?.error || "Copy failed");
      setResults({});
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2.5">
          <span className="text-[15px] font-semibold">Copy to Other Environments</span>
          <span className="modal-name-chip">{record.name}</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={580}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{done ? "Close" : "Cancel"}</Button>
          {!done && (
            <Button type="primary" onClick={handleCopy} loading={saving}>
              Copy
            </Button>
          )}
        </Space>
      }
    >
      {/* Source */}
      <div className="section-box mb-4">
        <div className="mini-label mb-2">Source</div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className={`env-pill ${currentEnv}`}>{ENV_META[currentEnv]?.short}</span>
          <span className="text-[11px] text-slate-500">Tenant:</span>
          <span className="text-[12px] font-medium">{tenantSchema}</span>
          <span className="value-chip max-w-[220px]">
            {sourceValue || <span className="text-slate-400">empty</span>}
          </span>
        </div>
      </div>

      {/* Top-level: if already exists */}
      {!done && (
        <div className="section-box mb-4">
          <div className="mini-label mb-2">
            If config already exists in target
          </div>
          <Radio.Group value={ifExists} onChange={(e) => setIfExists(e.target.value)} size="small">
            <Radio value="override">
              <span className="text-[12px] font-medium">Override</span>
              <span className="text-[11px] text-slate-400 ml-1">— replace existing value</span>
            </Radio>
            <Radio value="skip">
              <span className="text-[12px] font-medium">Skip</span>
              <span className="text-[11px] text-slate-400 ml-1">— keep existing, don't change</span>
            </Radio>
          </Radio.Group>
        </div>
      )}

      {/* Select all */}
      {!done && (
        <div className="flex items-center justify-between mb-2.5">
          <Checkbox
            checked={otherEnvs.filter((e) => !envUnavailable(e)).every((e) => envStates[e]?.selected)}
            indeterminate={
              otherEnvs.some((e) => !envStates[e]?.selected && !envUnavailable(e)) &&
              otherEnvs.some((e) => envStates[e]?.selected)
            }
            onChange={(e) => {
              setEnvStates((prev) => {
                const next = { ...prev };
                otherEnvs.forEach((env) => {
                  if (!next[env]?.loadError && !next[env]?.noTargets) next[env] = { ...next[env], selected: e.target.checked };
                });
                return next;
              });
            }}
          >
            <span className="text-[12px] font-semibold text-slate-600">Select All</span>
          </Checkbox>
          <span className="text-[11px] text-slate-400">
            {otherEnvs.filter((e) => envStates[e]?.selected).length} of {otherEnvs.length} selected
          </span>
        </div>
      )}

      {/* Target envs */}
      <div className="flex flex-col gap-2.5">
        {otherEnvs.map((env) => {
          const meta      = ENV_META[env];
          const state     = envStates[env] || {};
          const resultObj = results[env];
          const status    = resultObj?.overall;
          const unchecked = !state.selected;

          return (
            <div key={env} className={`copy-target-card px-4 py-3.5${unchecked ? " is-unchecked" : ""}${unchecked && !status ? " is-dimmed" : ""}`}>
              {/* Top row */}
              <div className={`flex items-center justify-between ${unchecked || status ? "" : "mb-3"}`}>
                <div className="flex items-center gap-2">
                  {!status && (
                    <Checkbox
                      checked={state.selected}
                      disabled={state.loadError || state.noTargets}
                      onChange={(e) => setEnvField(env, "selected", e.target.checked)}
                    />
                  )}
                  <span className={`env-pill ${env}`}>{meta.short}</span>
                  <span className="text-[12px] text-slate-500">{meta.label}</span>
                  {state.loadError && (
                    <Tooltip title={state.loadErrorMessage || "Failed to load tenants"} placement="top">
                      <span className="text-[11px] text-amber-600 flex items-center gap-1 cursor-help">
                        <WarningOutlined className="text-[12px]" /> Unavailable
                      </span>
                    </Tooltip>
                  )}
                  {state.noTargets && (
                    <span className="text-[11px] text-slate-400">No tenants you can copy to</span>
                  )}
                </div>

                {status && (
                  <div className="flex items-center gap-1.5">
                    <StatusIcon status={status} />
                    <span className={`text-[12px] font-medium capitalize ${STATUS_TEXT[status] || "text-emerald-600"}`}>
                      {status === "loading" ? "Copying…" : status}
                    </span>
                    {status !== "loading" && status !== "skipped" && resultObj?.summary && (
                      <span className="text-[11px] text-slate-400">
                        {[
                          resultObj.summary.created && `${resultObj.summary.created} created`,
                          resultObj.summary.updated && `${resultObj.summary.updated} updated`,
                          resultObj.summary.skipped && `${resultObj.summary.skipped} skipped`,
                          resultObj.summary.error   && `${resultObj.summary.error} error${resultObj.summary.error > 1 ? "s" : ""}`,
                        ].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Tenants + value — shown only when selected */}
              {state.selected && !status && (
                <div className="flex flex-col gap-2 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Select
                      mode="multiple"
                      placeholder="Select target tenants..."
                      loading={state.loadingTenants}
                      options={(state.tenants || []).map((t) => ({ label: t.subdomain, value: t.name }))}
                      onChange={(val) => {
                        setEnvStates((prev) => {
                          const current = prev[env] || {};
                          const nextValues = { ...(current.tenantValues || {}) };
                          val.forEach((t) => { if (!(t in nextValues)) nextValues[t] = sourceValue; });
                          return { ...prev, [env]: { ...current, tenantSchemas: val, tenantValues: nextValues } };
                        });
                      }}
                      value={state.tenantSchemas || []}
                      showSearch
                      filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                      className="flex-1 min-w-0"
                      size="small"
                      maxTagCount="responsive"
                      allowClear
                    />
                    <Tooltip title={`Select all ${(state.tenants || []).length} tenants`}>
                      <Button
                        size="small"
                        onClick={() => {
                          setEnvStates((prev) => {
                            const current = prev[env] || {};
                            const allNames = (current.tenants || []).map((t) => t.name);
                            const nextValues = { ...(current.tenantValues || {}) };
                            allNames.forEach((t) => { if (!(t in nextValues)) nextValues[t] = sourceValue; });
                            return { ...prev, [env]: { ...current, tenantSchemas: allNames, tenantValues: nextValues } };
                          });
                        }}
                        disabled={(state.tenants || []).length === 0 || state.loadingTenants}
                      >
                        All
                      </Button>
                    </Tooltip>
                  </div>
                  {(state.tenants || []).length > 0 && (
                    <div className="text-[11px] text-slate-400">
                      {(state.tenantSchemas || []).length} of {(state.tenants || []).length} tenants selected
                      {(state.tenantSchemas || []).length > 0 && " · value defaults to source — override per-tenant if needed"}
                    </div>
                  )}
                  {(state.tenantSchemas || []).length > 0 && (
                    <div className="flex flex-col gap-1.5 max-h-[240px] overflow-y-auto pr-1">
                      {(state.tenantSchemas || []).map((tName) => {
                        const tenant = (state.tenants || []).find((t) => t.name === tName) || { name: tName, subdomain: tName };
                        const label = tenant.subdomain || tenant.name;
                        return (
                          <div key={tName} className="flex items-start gap-2 shrink-0">
                            <Tooltip title={tenant.name !== label ? tenant.name : null}>
                              <span className="text-[12px] font-medium text-slate-600 min-w-[110px] max-w-[110px] pt-[5px] truncate">
                                {label}
                              </span>
                            </Tooltip>
                            <Input.TextArea
                              value={state.tenantValues?.[tName] ?? sourceValue}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                setEnvStates((prev) => ({
                                  ...prev,
                                  [env]: {
                                    ...prev[env],
                                    tenantValues: { ...(prev[env]?.tenantValues || {}), [tName]: newValue },
                                  },
                                }));
                              }}
                              autoSize={{ minRows: 1, maxRows: 3 }}
                              placeholder="Value for this tenant..."
                              className="flex-1 !text-[13px]"
                              size="small"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Per-tenant result rows */}
              {status && status !== "skipped" && resultObj?.perTenant?.length > 0 && (
                <div className="mt-2.5 max-h-[180px] overflow-y-auto flex flex-col gap-1">
                  {resultObj.perTenant.map((r) => (
                    <Tooltip key={r.tenant} title={r.status === "error" ? (r.error || "Unknown error") : null} placement="left">
                      <div className={`flex items-center gap-1.5 text-[12px] py-0.5 ${r.status === "error" ? "cursor-help" : "cursor-default"}`}>
                        <StatusIcon status={r.status} />
                        <span className="text-slate-600 font-medium flex-1 truncate">
                          {r.tenant}
                        </span>
                        <span className={`capitalize text-[11px] ${STATUS_TEXT[r.status] || "text-emerald-600"}`}>
                          {r.status}
                        </span>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
